import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import { useData } from '../context/DataContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Save, 
  Plus, 
  Trash2, 
  Edit3, 
  Users, 
  X,
  FileText,
  Briefcase,
  Sliders,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, setDoc, doc, deleteDoc, onSnapshot } from 'firebase/firestore';
import Swal from 'sweetalert2';

const AdminAbout = () => {
  const { data } = useData();
  const chairman = data.about.chairman || { name: '', position: '', message: '', image: '' };

  const [directors, setDirectors] = useState([]);
  const [editingDirector, setEditingDirector] = useState(null);
  const [uploadedDirectorUrl, setUploadedDirectorUrl] = useState('');
  const [activeTab, setActiveTab] = useState('chairman'); // 'chairman' | 'directors'

  const [isEditingChairman, setIsEditingChairman] = useState(false);
  const [chairmanLocal, setChairmanLocal] = useState({ name: '', position: '', message: '', image: '' });

  // Sync local chairman details when database context changes
  useEffect(() => {
    if (data?.about?.chairman) {
      setChairmanLocal(data.about.chairman);
    }
  }, [data]);

  // Listen to the Directors collection
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'directors'), (snapshot) => {
      setDirectors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Control body scrolling when editing a director modal is open
  useEffect(() => {
    if (editingDirector) {
      setUploadedDirectorUrl(editingDirector.image || '');
      document.body.classList.add('admin-modal-open');
    } else {
      document.body.classList.remove('admin-modal-open');
    }
    return () => document.body.classList.remove('admin-modal-open');
  }, [editingDirector]);

  // Save Chairman Info to Firestore
  const handleSaveChairman = async () => {
    try {
      await setDoc(doc(db, 'site_content', 'about'), { chairman: chairmanLocal }, { merge: true });
      setIsEditingChairman(false);
      Swal.fire({
        title: 'Success',
        text: 'Chairman profile information updated successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
    } catch (error) {
      Swal.fire('Error', 'Failed to update chairman info', 'error');
    }
  };

  // Save Director Info to Firestore (without email field)
  const handleSaveDirector = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const directorData = {
      name: formData.get('name'),
      role: formData.get('role'),
      desc: formData.get('desc'),
      image: uploadedDirectorUrl
    };

    Swal.fire({
      title: 'Saving Director Profile...',
      text: 'Updating board listings',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      if (editingDirector?.id) {
        await setDoc(doc(db, 'directors', editingDirector.id), directorData);
      } else {
        await addDoc(collection(db, 'directors'), directorData);
      }
      
      await Swal.fire({
        title: 'Success',
        text: 'Director profile saved successfully',
        icon: 'success',
        confirmButtonColor: '#2d6a4f',
      });
      setEditingDirector(null);
    } catch (error) {
      console.error(error);
      Swal.fire('Error', 'Failed to save director info', 'error');
    }
  };

  // Delete Director from Firestore
  const handleDeleteDirector = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will permanently remove this director from the active board roster!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove them'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'directors', id));
        Swal.fire({
          title: 'Removed!',
          text: 'Director has been removed from board listing.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        Swal.fire('Error', 'Failed to delete director', 'error');
      }
    }
  };

  return (
    <div className="space-y-8 pb-20 font-poppins">
      
      {/* Header Banner */}
      <div className="bg-sugarcane-charcoal p-6 rounded-2xl text-white flex flex-col md:flex-row justify-between items-center gap-6 relative overflow-hidden shadow-md">
        <div className="absolute top-0 left-0 w-64 h-64 bg-sugarcane-green/10 rounded-full -ml-32 -mt-32 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-700/10 rounded-full -mr-32 -mb-32 blur-3xl"></div>
        
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
              <Sliders size={20} />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">About Us Settings</h2>
          </div>
          <p className="text-gray-400 text-sm italic">Configure profile parameters for the Chairman and Board of Directors team.</p>
        </div>

        <div className="relative z-10 px-6 py-2.5 bg-white/5 rounded-xl border border-white/10 flex items-center space-x-4">
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sugarcane-green">Roster Count</p>
            <p className="text-sm font-semibold">{directors.length + (chairman.name ? 1 : 0)} Executive Profiles</p>
          </div>
          <div className="w-2.5 h-2.5 bg-sugarcane-green rounded-full animate-pulse"></div>
        </div>
      </div>

      {/* Tabs Switcher Control System */}
      <div className="flex bg-gray-100/80 p-1 rounded-2xl max-w-xl mx-auto border border-gray-200/40 shadow-inner backdrop-blur-md">
        <button
          onClick={() => setActiveTab('chairman')}
          className={`flex-1 py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all duration-300 flex items-center justify-center space-x-2.5 ${
            activeTab === 'chairman'
              ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10'
              : 'text-gray-400 hover:text-sugarcane-charcoal'
          }`}
        >
          <User size={16} />
          <span>Founder / Chairman Info</span>
        </button>
        <button
          onClick={() => setActiveTab('directors')}
          className={`flex-1 py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all duration-300 flex items-center justify-center space-x-2.5 ${
            activeTab === 'directors'
              ? 'bg-sugarcane-green text-white shadow-md shadow-sugarcane-green/10'
              : 'text-gray-400 hover:text-sugarcane-charcoal'
          }`}
        >
          <Users size={16} />
          <span>Board of Directors</span>
        </button>
      </div>

      {/* Active Section Content Panels */}
      <AnimatePresence mode="wait">
        {activeTab === 'chairman' ? (
          <motion.section
            key="chairman-section"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-10 h-10 bg-sugarcane-green rounded-xl flex items-center justify-center text-white">
                <User size={18} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-sugarcane-charcoal">Founder / Chairman Profile Management</h3>
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Update public message, name, and profile image</p>
              </div>
            </div>

            {/* Split Grid - Constraining Image Upload size */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              
              {/* Form Inputs (Left & Center Columns) */}
              <div className="lg:col-span-2 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold tracking-wider text-gray-500 mb-2">Chairman Name</label>
                    <input
                      type="text"
                      value={chairmanLocal.name}
                      onChange={(e) => setChairmanLocal({ ...chairmanLocal, name: e.target.value })}
                      placeholder="e.g. Parshwa Chaougule"
                      disabled={!isEditingChairman}
                      className="w-full bg-gray-50/50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none disabled:bg-gray-100/70 disabled:text-gray-400 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold tracking-wider text-gray-500 mb-2">Position Title</label>
                    <input
                      type="text"
                      value={chairmanLocal.position}
                      onChange={(e) => setChairmanLocal({ ...chairmanLocal, position: e.target.value })}
                      placeholder="e.g. Founder & Chairman"
                      disabled={!isEditingChairman}
                      className="w-full bg-gray-50/50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none disabled:bg-gray-100/70 disabled:text-gray-400 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-wider text-gray-500 mb-2">Message / Statement</label>
                  <textarea
                    rows="6"
                    value={chairmanLocal.message}
                    onChange={(e) => setChairmanLocal({ ...chairmanLocal, message: e.target.value })}
                    placeholder="Provide the official inspirational statement displayed on the About page..."
                    disabled={!isEditingChairman}
                    className="w-full bg-gray-50/50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-600 focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none leading-relaxed disabled:bg-gray-100/70 disabled:text-gray-400 disabled:cursor-not-allowed"
                  ></textarea>
                </div>
              </div>

              {/* Upload image constrained container (Right Column) */}
              <div className="lg:col-span-1 bg-gray-50/50 border border-gray-200 rounded-xl p-5 flex flex-col justify-center">
                <div className="w-full max-w-[260px] mx-auto space-y-4">
                  <div className="text-center mb-2">
                    <label className="block text-xs font-semibold tracking-wider text-gray-500">Profile Picture</label>
                    <p className="text-[9px] text-gray-400 mt-0.5">Will render inside 3:4 aspect ratio</p>
                  </div>
                  
                  {/* Compact container limiting max image dimensions */}
                  <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                    <ImageUpload
                      folder="chairman"
                      initialImage={chairmanLocal.image}
                      onUploadSuccess={(url) => setChairmanLocal({ ...chairmanLocal, image: url })}
                      disabled={!isEditingChairman}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Save Buttons */}
            <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end">
              {isEditingChairman ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setChairmanLocal(chairman);
                      setIsEditingChairman(false);
                    }}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all mr-3 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveChairman}
                    className="bg-sugarcane-green hover:bg-sugarcane-charcoal text-white px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md shadow-sugarcane-green/10 cursor-pointer"
                  >
                    <Save size={16} />
                    <span>Save Chairman Profile</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditingChairman(true)}
                  className="bg-sugarcane-green hover:bg-sugarcane-charcoal text-white px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-md shadow-sugarcane-green/10 cursor-pointer"
                >
                  <Edit3 size={16} />
                  <span>Edit Chairman Profile</span>
                </button>
              )}
            </div>
          </motion.section>
        ) : (
          <motion.section
            key="directors-section"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
            className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100"
          >
            {/* Header row */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-6 border-b border-gray-100">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white">
                  <Users size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-sugarcane-charcoal">Board of Directors Listing</h3>
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Manage board members, profiles and descriptions</p>
                </div>
              </div>
              <button
                onClick={() => setEditingDirector({})}
                className="bg-sugarcane-charcoal hover:bg-sugarcane-green text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center space-x-2 transition-all shadow-sm"
              >
                <Plus size={16} />
                <span>Add Director Profile</span>
              </button>
            </div>

            {/* Empty check */}
            {directors.length === 0 ? (
              <div className="py-20 text-center max-w-sm mx-auto">
                <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-100 shadow-inner">
                  <HelpCircle className="text-gray-300" size={28} />
                </div>
                <h4 className="text-md font-bold text-sugarcane-charcoal mb-1">No Directors Found</h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  Start building the leadership catalog by clicking the Add Director button above.
                </p>
              </div>
            ) : (
              /* Directors Cards list (Cleaned from email representation) */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {directors.map((director) => (
                  <motion.div
                    key={director.id}
                    layout
                    whileHover={{ y: -4 }}
                    transition={{ type: 'tween', duration: 0.3 }}
                    className="bg-white p-5 rounded-xl border border-gray-100 flex flex-col justify-between group shadow-sm hover:shadow-md hover:border-sugarcane-green/30 transition-all duration-300"
                  >
                    <div>
                      {/* Avatar Header */}
                      <div className="flex items-center space-x-4 mb-4">
                        <img 
                          src={director.image || 'https://via.placeholder.com/150'} 
                          alt={director.name} 
                          className="w-14 h-14 rounded-xl object-cover shadow-sm border border-gray-100 group-hover:scale-105 transition-transform" 
                        />
                        <div>
                          <h5 className="font-bold text-sugarcane-charcoal line-clamp-1 group-hover:text-sugarcane-green transition-colors">
                            {director.name}
                          </h5>
                          <p className="text-[9px] text-sugarcane-green font-bold uppercase tracking-wider">
                            {director.role}
                          </p>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <p className="text-xs text-gray-500 leading-relaxed italic mb-5">
                        "{director.desc}"
                      </p>
                    </div>

                    {/* Bottom controls row (Without Email tags) */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
                        <Briefcase size={12} className="text-gray-300" />
                        Executive board
                      </span>
                      <div className="flex space-x-1.5">
                        <button
                          onClick={() => setEditingDirector(director)}
                          className="p-2 bg-gray-50 text-blue-500 hover:bg-blue-500 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center"
                          title="Edit Profile"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteDirector(director.id)}
                          className="p-2 bg-gray-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all shadow-sm flex items-center justify-center"
                          title="Delete Director"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Modal - Edit/Add Director Form (Cleaned from email input) */}
            <AnimatePresence>
              {editingDirector && (
                <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 bg-sugarcane-charcoal/80 backdrop-blur-md admin-modal-overlay">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-white rounded-2xl w-full max-w-5xl shadow-2xl relative overflow-hidden border border-gray-100"
                  >
                    {/* Header Banner */}
                    <div className="sticky top-0 bg-white/95 backdrop-blur-md px-6 py-5 border-b border-gray-100 flex justify-between items-center z-10">
                      <div>
                        <span className="px-3 py-1 bg-sugarcane-green/5 rounded-full text-[9px] font-bold uppercase tracking-widest text-sugarcane-green border border-sugarcane-green/10">
                          Board Registry
                        </span>
                        <h3 className="text-xl font-bold mt-1.5 tracking-tight text-sugarcane-charcoal">
                          {editingDirector.id ? 'Edit Board Member' : 'Add New Director'}
                        </h3>
                      </div>
                      <button 
                        onClick={() => setEditingDirector(null)} 
                        className="p-2 bg-gray-50 hover:bg-gray-100 rounded-xl text-sugarcane-charcoal transition-colors cursor-pointer"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    {/* Form Body */}
                    <form onSubmit={handleSaveDirector} className="p-6 space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                        
                        {/* Info details (2 cols) */}
                        <div className="lg:col-span-2 space-y-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Full Name</label>
                              <input 
                                name="name" 
                                defaultValue={editingDirector.name} 
                                required 
                                placeholder="e.g. Aditya Patil"
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Position Role</label>
                              <input 
                                name="role" 
                                defaultValue={editingDirector.role} 
                                required 
                                placeholder="e.g. Executive Director"
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner" 
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">Short Bio / Quote Statement</label>
                            <textarea 
                              name="desc" 
                              defaultValue={editingDirector.desc} 
                              required 
                              rows="5" 
                              placeholder="Provide a brief summary quote reflecting their vision for the sugarcane company..."
                              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all outline-none text-sugarcane-charcoal shadow-inner resize-none h-32"
                            ></textarea>
                          </div>
                        </div>

                        {/* Image Upload card (1 col) */}
                        <div className="lg:col-span-1 bg-gray-50 border border-gray-200 rounded-2xl p-6 flex flex-col justify-center items-center shadow-sm">
                          <div className="w-full max-w-[240px] mx-auto space-y-4">
                            <div className="text-center mb-2">
                              <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">Director Photo</label>
                              <p className="text-[10px] text-gray-400 mt-1 font-medium">Optimized dimensions</p>
                            </div>
                            
                            {/* Constrained layout for image picker */}
                            <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
                              <ImageUpload
                                folder="director"
                                initialImage={uploadedDirectorUrl}
                                onUploadSuccess={setUploadedDirectorUrl}
                              />
                            </div>
                          </div>
                        </div>

                      </div>

                      {/* Controls Footer */}
                      <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                        <button 
                          type="button" 
                          onClick={() => setEditingDirector(null)} 
                          className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition-all cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="px-6 py-2.5 bg-sugarcane-green hover:bg-sugarcane-charcoal text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-sugarcane-green/10 flex items-center gap-2 cursor-pointer"
                        >
                          <Plus size={16} />
                          <span>{editingDirector.id ? 'Save Changes' : 'Confirm Member'}</span>
                        </button>
                      </div>
                    </form>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAbout;
